import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { apiConf } from '../../../config/api.config';
import { JWTToken } from '../../../modules/users/models/tokens';
import { IAuthService } from '../../../modules/users/services/authService';

interface RetryableRequest extends InternalAxiosRequestConfig {
  _isRetry?: boolean;
}

const HTTP_STATUS = {
  UNAUTHORIZED: 401,
  NOT_FOUND: 404,
} as const;

const AUTH_CONSTANTS = {
  BEARER_PREFIX: 'Bearer ',
  TOKEN_REFRESH_URL: '/v1/users/token/refresh',
  RETRY_DELAY: 500,
} as const;

type AccessResponse = {
  accessToken: JWTToken;
  exp: number;
};

export interface IHTTPClientRequestParams<T> {
  url: string;
  requiresToken: boolean;
  payload?: T;
  config?: AxiosRequestConfig;
}

export abstract class BaseAPI {
  private instance: AxiosInstance | null = null;
  public readonly authService: IAuthService;
  private isRefreshing: boolean;
  private refreshQueue: Array<{
    resolve: (value: any) => void;
    reject: (error: any) => void;
  }> = [];

  protected abstract onFailureExecuteImpl(
    error: AxiosError,
  ): Promise<void | any>;

  constructor(authService: IAuthService) {
    this.authService = authService;
    this.isRefreshing = false;
  }

  private get http(): AxiosInstance {
    return this.instance !== null ? this.instance : this.initHttp();
  }

  private initHttp() {
    const http = axios.create({
      baseURL: apiConf.baseUrl,
      withCredentials: true,
      timeout: apiConf.timeout || 30000,
    });

    this.enableInterceptors(http);
    this.instance = http;
    return http;
  }

  private addDevelopmentDelay(): Promise<void> {
    if (apiConf.enableApiDelay && process.env.NODE_ENV === 'development') {
      console.debug(
        `Adding artificial delay of ${
          apiConf.delay ? apiConf.delay : 0
        }ms to API request`,
      );
      return new Promise((resolve) =>
        setTimeout(resolve, apiConf.delay ? apiConf.delay : 0),
      );
    }
    return Promise.resolve();
  }

  private getDefaultHeaders(): Record<string, string> {
    return {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      Expires: '0',
    };
  }

  public cancelPendingRequests(message = 'Operation canceled'): void {
    const source = axios.CancelToken.source();
    source.cancel(message);
  }

  private createRequestConfig(
    params: IHTTPClientRequestParams<any>,
  ): AxiosRequestConfig {
    const { requiresToken, config = {} } = params;
    const { headers: configHeaders = {}, ...restConfig } = config;

    return {
      ...restConfig,
      headers: {
        ...configHeaders,
        ...this.getDefaultHeaders(),
        ...(requiresToken && {
          Authorization: this.createAuthorizationHeader(),
        }),
      },
    };
  }

  private isTokenExpired(): boolean {
    const expiration = this.authService.getAccessExp();
    if (!expiration) return true;

    return expiration * 1000 - 30000 < Date.now();
  }

  private createAuthorizationHeader(): string {
    const token = this.authService.getAccessToken();
    return token ? `${AUTH_CONSTANTS.BEARER_PREFIX}${token}` : '';
  }

  private enableInterceptors(http: AxiosInstance): void {
    http.interceptors.request.use(
      async (config) => {
        await this.addDevelopmentDelay();
        if (
          config.headers.Authorization &&
          this.isTokenExpired() &&
          !config.url?.includes(AUTH_CONSTANTS.TOKEN_REFRESH_URL)
        ) {
          try {
            await this.refreshAccessToken();
            config.headers.Authorization = this.createAuthorizationHeader();
          } catch (error) {
            console.error('Failed to refresh token proactively', error);
          }
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    http.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const config = error.config as RetryableRequest;

        if (
          error.response?.status === HTTP_STATUS.NOT_FOUND &&
          !config._isRetry &&
          !config.url?.includes(AUTH_CONSTANTS.TOKEN_REFRESH_URL)
        ) {
          config._isRetry = true;

          try {
            await this.refreshAccessToken();

            config.headers.Authorization = this.createAuthorizationHeader();

            return this.http.request(config);
          } catch (refreshError) {
            return Promise.reject(refreshError);
          }
        }

        return this.executeOnFailure(error);
      },
    );
  }

  private async refreshAccessToken(): Promise<void> {
    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.refreshQueue.push({ resolve, reject });
      });
    }

    this.isRefreshing = true;

    try {
      const [accessToken, exp] =
        await this.regenerateAccessTokenFromRefreshToken();
      this.authService.setAccessToken(accessToken);
      this.authService.setAccessExp(exp);

      this.processRefreshQueue(null);
      return;
    } catch (error) {
      this.authService.removeAccessToken();
      this.processRefreshQueue(error);
      throw error;
    } finally {
      this.isRefreshing = false;
    }
  }

  private processRefreshQueue(error: any): void {
    while (this.refreshQueue.length) {
      const request = this.refreshQueue.shift();
      if (error) {
        request?.reject(error);
      } else {
        request?.resolve(null);
      }
    }
  }

  private async regenerateAccessTokenFromRefreshToken(
    retries = 2,
  ): Promise<[JWTToken, number]> {
    try {
      const { data } = await this.post<AccessResponse>({
        url: AUTH_CONSTANTS.TOKEN_REFRESH_URL,
        requiresToken: false,
      });

      return [data.accessToken, data.exp];
    } catch (error) {
      if (retries > 0) {
        await new Promise((resolve) =>
          setTimeout(resolve, AUTH_CONSTANTS.RETRY_DELAY),
        );
        return this.regenerateAccessTokenFromRefreshToken(retries - 1);
      }
      throw error;
    }
  }

  protected request<T = any, R = AxiosResponse<T>>(
    config: AxiosRequestConfig,
  ): Promise<R> {
    return this.http.request(config);
  }

  protected get<T = any, R = AxiosResponse<T>, P = any>(
    params: IHTTPClientRequestParams<P>,
  ): Promise<R> {
    const options = this.createRequestConfig(params);
    return this.http.get<T, R>(params.url, options);
  }

  protected async post<T = any, R = AxiosResponse<T>, P = any>(
    params: IHTTPClientRequestParams<P>,
  ): Promise<R> {
    const options = this.createRequestConfig(params);
    return this.http.post<T, R>(params.url, params.payload, options);
  }

  protected async patch<T = any, R = AxiosResponse<T>, P = any>(
    params: IHTTPClientRequestParams<P>,
  ): Promise<R> {
    const options = this.createRequestConfig(params);
    return this.http.patch<T, R>(params.url, params.payload, options);
  }

  protected async delete<T = any, R = AxiosResponse<T>, P = any>(
    params: IHTTPClientRequestParams<P>,
  ): Promise<R> {
    const options = this.createRequestConfig(params);
    return this.http.delete<T, R>(params.url, options);
  }

  private async executeOnFailure(error: AxiosError): Promise<void> {
    try {
      await this.onFailureExecuteImpl(error);
    } catch (err) {
      return Promise.reject({ ...error });
    }
  }
}
